import React, { useState } from 'react';
import { requestOtp, verifyOtpCode, errText } from '../services/api';
import { pendingOtpChannel } from '../utils/otpPending';
import { T } from '../styles/ds';

// PH-47 / ADR-010 — one OTP round-trip: pick a recovery channel, send, enter the code, verify.
// Used by ResetStaffPin.jsx's "Forgot PIN" path. It proves exactly one OTP and hands the token to
// the caller's `onVerified(otpToken, channel)`. (PH-110 / ADR-016 removed `excludeChannel`: it
// only served the retired second-channel step of an Admin's reset.)
function OtpVerify({ config, onVerified, onCancel, title, subtitle }) {
  const [step, setStep] = useState('choose'); // 'choose' | 'enter'
  const [channel, setChannel] = useState(null);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const maskedPhone = config?.recoveryPhone || null;
  const maskedEmail = config?.recoveryEmail || null;
  const showPhone = !!maskedPhone;
  const showEmail = !!maskedEmail;

  const send = async (via) => {
    setLoading(true); setError(''); setNotice('');
    try {
      await requestOtp(via);
      setChannel(via);
      setStep('enter');
    } catch (err) {
      // PH-75 — a code sent earlier (possibly to the OTHER channel) is still valid, so no new one
      // went out. Open the code box for the channel it actually went to: verify-otp checks that
      // channel, and the "sent to" line must name the right contact.
      const pending = pendingOtpChannel(err);
      if (pending) {
        setChannel(pending);
        setOtp('');
        setNotice('पहले भेजा गया OTP अभी भी मान्य है — वही नीचे डालें / A code sent earlier is still valid — enter it below');
        setStep('enter');
      } else {
        setError(errText(err, 'OTP delivery failed — try again'));
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(otp)) { setError('6 अंकों का OTP डालें / Enter 6-digit OTP'); return; }
    setLoading(true); setError('');
    try {
      const res = await verifyOtpCode(otp, channel);
      onVerified(res.data.otp_token, channel);
    } catch (err) {
      setError(errText(err, 'Incorrect OTP'));
      setLoading(false);
    }
  };

  if (step === 'choose') {
    return (
      <>
        {title && <div style={{ fontSize: 18, fontWeight: 800, color: T.ink, textAlign: 'center' }}>{title}</div>}
        {subtitle && <div style={{ fontSize: 13, color: T.mute, marginTop: 4, marginBottom: 20, textAlign: 'center' }}>{subtitle}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {showPhone && (
            <button type="button" onClick={() => send('phone')} disabled={loading}
              style={{ padding: '14px', background: T.blue.bg, border: `2px solid ${T.ink}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: loading ? 'default' : 'pointer', color: T.ink, textAlign: 'left', opacity: loading ? 0.6 : 1 }}>
              📱 SMS — {maskedPhone}
            </button>
          )}
          {showEmail && (
            <button type="button" onClick={() => send('email')} disabled={loading}
              style={{ padding: '14px', background: T.blue.bg, border: `2px solid ${T.ink}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: loading ? 'default' : 'pointer', color: T.ink, textAlign: 'left', opacity: loading ? 0.6 : 1 }}>
              ✉️ Email — {maskedEmail}
            </button>
          )}
          {!showPhone && !showEmail && (
            <div role="alert" style={{ color: T.red.text, fontSize: 13, textAlign: 'center' }}>
              कोई रिकवरी संपर्क उपलब्ध नहीं — मालिक से Settings में जोड़ने को कहें<br />
              <span style={{ color: T.mute, fontSize: 12 }}>No recovery contact available — ask the owner to add one in Settings</span>
            </div>
          )}
        </div>
        {error && <div role="alert" style={{ color: T.red.text, fontSize: 12, marginTop: 8, textAlign: 'center' }}>{error}</div>}
        {onCancel && (
          <button onClick={onCancel} style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: T.mute, fontSize: 13, cursor: 'pointer' }}>
            ← वापस / Back
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>OTP डालें / Enter OTP</div>
        <div style={{ fontSize: 13, color: T.mute, marginTop: 4 }}>
          भेजा गया: {channel === 'phone' ? (maskedPhone || 'SMS') : (maskedEmail || 'Email')}
        </div>
        {notice && <div role="status" style={{ fontSize: 13, color: T.sec, marginTop: 8 }}>{notice}</div>}
      </div>
      <input
        autoFocus
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={otp}
        onChange={e => { setOtp(e.target.value); setError(''); }}
        placeholder="• • • • • •"
        aria-label="OTP"
        style={{
          width: '100%', padding: '14px', border: `2px solid ${error ? T.red.text : T.border}`,
          borderRadius: 8, fontSize: 24, textAlign: 'center', letterSpacing: 10,
          boxSizing: 'border-box', outline: 'none', marginBottom: 8,
        }}
      />
      {error && <div role="alert" style={{ color: T.red.text, fontSize: 12, marginBottom: 8, textAlign: 'center' }}>{error}</div>}
      <button onClick={verify} disabled={loading}
        style={{ width: '100%', padding: '13px', background: T.ink, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
        {loading ? 'जाँच हो रही है...' : 'सत्यापित करें / Verify →'}
      </button>
      <button onClick={() => { setStep('choose'); setOtp(''); setError(''); setNotice(''); }} style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: T.mute, fontSize: 13, cursor: 'pointer' }}>
        ← वापस / Back
      </button>
    </>
  );
}

export default OtpVerify;
